import { GanttTask, GanttConfig } from '../types/gantt';
import { calculateTimelineBounds, formatDateToIso } from './dateUtils';

export interface AdaptiveCardPayload {
  $schema: string;
  type: string;
  version: string;
  metadata?: {
    webUrl: string;
  };
  body: any[];
  actions: any[];
}

/**
 * Generates an official Microsoft Adaptive Card (v1.5) JSON payload
 * configured for Microsoft Loop, Microsoft Teams, and Outlook.
 */
export function generateAdaptiveCardPayload(
  tasks: GanttTask[],
  config: GanttConfig,
  chartDataUrl?: string,
  baseUrl = 'https://gantt.wdmr.dev'
): AdaptiveCardPayload {
  const bounds = calculateTimelineBounds(tasks, config.timeScale, config.weekStartDay || 'monday');

  const totalTasks = tasks.length;
  const completedTasks = tasks.filter((t) => (t.progress || 0) === 100).length;
  const overallProgress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  // Encode state into hash URL for Loop component portability
  const statePayload = encodeURIComponent(
    JSON.stringify({
      t: tasks.map((t) => [t.activity, t.start, t.end, t.progress || 100]),
      c: config.title,
    })
  );
  const shareableUrl = `${baseUrl}#card=${statePayload}`;

  const body: any[] = [];

  // 1. Header Container
  body.push({
    type: 'Container',
    style: 'emphasis',
    bleed: true,
    items: [
      {
        type: 'ColumnSet',
        columns: [
          {
            type: 'Column',
            width: 'auto',
            items: [
              {
                type: 'Image',
                url: `${baseUrl}/assets/icon-32.png`,
                size: 'Small',
                altText: 'Gantt Chart Icon',
              },
            ],
          },
          {
            type: 'Column',
            width: 'stretch',
            items: [
              {
                type: 'TextBlock',
                text: config.title || 'Project Roadmap',
                weight: 'Bolder',
                size: 'Medium',
                wrap: true,
              },
              {
                type: 'TextBlock',
                text: `${bounds.startDate} to ${bounds.endDate} • ${totalTasks} Activities • ${overallProgress}% Complete`,
                isSubtle: true,
                size: 'Small',
                spacing: 'None',
              },
            ],
          },
        ],
      },
    ],
  });

  // 2. Visual Gantt Chart Image (if data URL provided)
  if (chartDataUrl) {
    body.push({
      type: 'Container',
      spacing: 'Medium',
      items: [
        {
          type: 'Image',
          url: chartDataUrl,
          altText: 'Visual Gantt Chart Timeline',
          size: 'Auto',
          horizontalAlignment: 'Center',
        },
      ],
    });
  }

  // 3. Task List Table / Rows
  const tableRows: any[] = [
    {
      type: 'TableRow',
      style: 'emphasis',
      cells: [
        {
          type: 'TableCell',
          items: [{ type: 'TextBlock', text: 'Activity', weight: 'Bolder', size: 'Small' }],
        },
        {
          type: 'TableCell',
          items: [{ type: 'TextBlock', text: 'Start', weight: 'Bolder', size: 'Small' }],
        },
        {
          type: 'TableCell',
          items: [{ type: 'TextBlock', text: 'End', weight: 'Bolder', size: 'Small' }],
        },
        {
          type: 'TableCell',
          items: [{ type: 'TextBlock', text: 'Status', weight: 'Bolder', size: 'Small' }],
        },
      ],
    },
  ];

  tasks.forEach((task) => {
    const isMilestone = task.start === task.end;
    const progress = task.progress ?? 100;
    let statusText = `${progress}%`;
    let statusColor: string = 'Default';

    if (isMilestone) {
      statusText = 'Milestone';
      statusColor = 'Accent';
    } else if (progress === 100) {
      statusText = 'Done';
      statusColor = 'Good';
    } else if (progress > 0) {
      statusText = `${progress}% In Progress`;
      statusColor = 'Warning';
    } else {
      statusText = 'Not Started';
      statusColor = 'Attention';
    }

    tableRows.push({
      type: 'TableRow',
      cells: [
        {
          type: 'TableCell',
          items: [{ type: 'TextBlock', text: task.activity, size: 'Small', wrap: true }],
        },
        {
          type: 'TableCell',
          items: [{ type: 'TextBlock', text: task.start, size: 'Small', isSubtle: true }],
        },
        {
          type: 'TableCell',
          items: [{ type: 'TextBlock', text: task.end, size: 'Small', isSubtle: true }],
        },
        {
          type: 'TableCell',
          items: [
            {
              type: 'TextBlock',
              text: statusText,
              size: 'Small',
              color: statusColor,
              weight: 'Bolder',
            },
          ],
        },
      ],
    });
  });

  body.push({
    type: 'Table',
    spacing: 'Medium',
    columns: [
      { width: 4 },
      { width: 2 },
      { width: 2 },
      { width: 3 },
    ],
    rows: tableRows,
  });

  // 4. Action Buttons (Open Web App, Copy Card, Live URL)
  const actions: any[] = [
    {
      type: 'Action.OpenUrl',
      title: 'Open in Gantt Editor',
      url: shareableUrl,
      style: 'positive',
    },
    {
      type: 'Action.ShowCard',
      title: 'View Schedule Details',
      card: {
        type: 'AdaptiveCard',
        body: [
          {
            type: 'TextBlock',
            text: 'Timeline Statistics',
            weight: 'Bolder',
          },
          {
            type: 'FactSet',
            facts: [
              { title: 'Total Duration:', value: `${bounds.totalDays} Days` },
              { title: 'Time Scale:', value: bounds.scale.toUpperCase() },
              { title: 'Total Tasks:', value: `${totalTasks}` },
              { title: 'Completed Tasks:', value: `${completedTasks}` },
              { title: 'Generated:', value: formatDateToIso(new Date()) },
            ],
          },
        ],
      },
    },
  ];

  return {
    $schema: 'http://adaptivecards.io/schemas/adaptive-card.json',
    type: 'AdaptiveCard',
    version: '1.5',
    metadata: {
      webUrl: shareableUrl,
    },
    body,
    actions,
  };
}
